import { z } from 'zod';
import { PriceAdjustmentType } from '../../constants/price-adjustment-type.js';
import { PricingRuleApplyUomMode } from '../../constants/pricing-rule-apply-uom-mode.js';
import { optionalUuidString, requiredUuidString } from '../zod-helpers/index.js';
import { VOLUME_DISCOUNT_CONSTRAINTS } from './constraints.js';
const optionalId = (invalidMessage) => optionalUuidString(invalidMessage)
    .nullish()
    .transform((value) => (value?.trim() ? value : null));
// Omitted stays undefined so an update keeps the stored value, while an explicit null or blank sets it.
const keepableId = (invalidMessage) => optionalUuidString(invalidMessage)
    .nullish()
    .transform((value) => (value === undefined ? undefined : value?.trim() ? value : null));
const hasAtMostTwoDecimals = (value) => Number(value.toFixed(2)) === value;
export const volumeDiscountTierInputSchema = z.object({
    minCases: z
        .number()
        .int(`${VOLUME_DISCOUNT_CONSTRAINTS.minCases.label} must be a whole number.`)
        .min(1, `${VOLUME_DISCOUNT_CONSTRAINTS.minCases.label} must be at least 1.`),
    value: z
        .number()
        .positive(`${VOLUME_DISCOUNT_CONSTRAINTS.value.label} must be greater than 0.`)
        .max(VOLUME_DISCOUNT_CONSTRAINTS.value.maxFlat, `${VOLUME_DISCOUNT_CONSTRAINTS.value.label} must be ${VOLUME_DISCOUNT_CONSTRAINTS.value.maxFlat.toLocaleString()} or less.`)
        .refine(hasAtMostTwoDecimals, `${VOLUME_DISCOUNT_CONSTRAINTS.value.label} can have at most two decimal places.`),
});
// A product-target program has no category to exclude from, and a program targets either a category or a product.
export const saveProgramInputSchema = z
    .object({
    programId: optionalId('Invalid program selection.'),
    brandId: requiredUuidString('Brand is required.', 'Invalid brand selection.'),
    stateId: keepableId('Invalid state selection.'),
    productTreeId: optionalId('Invalid category selection.'),
    productId: optionalId('Invalid product selection.'),
    applyUomModeId: z.enum(PricingRuleApplyUomMode, 'Invalid unit mode selection.'),
    priceAdjustmentTypeId: z.enum(PriceAdjustmentType, 'Invalid markdown type selection.'),
    name: z
        .string()
        .trim()
        .max(VOLUME_DISCOUNT_CONSTRAINTS.name.maxLength, `${VOLUME_DISCOUNT_CONSTRAINTS.name.label} must be ${VOLUME_DISCOUNT_CONSTRAINTS.name.maxLength} characters or fewer.`)
        .nullish()
        .transform((value) => (value === undefined ? undefined : value || null)),
    tiers: z
        .array(volumeDiscountTierInputSchema)
        .min(VOLUME_DISCOUNT_CONSTRAINTS.tiers.min, 'Add at least one tier.')
        .max(VOLUME_DISCOUNT_CONSTRAINTS.tiers.max, `A program can have at most ${VOLUME_DISCOUNT_CONSTRAINTS.tiers.max} tiers.`),
    excludedProductIds: z
        .array(requiredUuidString('Product is required.', 'Invalid product selection.'))
        .max(VOLUME_DISCOUNT_CONSTRAINTS.excludedProductIds.max, `A program can exclude at most ${VOLUME_DISCOUNT_CONSTRAINTS.excludedProductIds.max} products.`)
        .nullish()
        .transform((value) => (value === null ? [] : value)),
})
    .superRefine((input, ctx) => {
    const excludedProductIds = input.excludedProductIds ?? [];
    if ((input.productTreeId === null) === (input.productId === null)) {
        ctx.addIssue({
            code: 'custom',
            message: 'Choose either a category or a product.',
            path: ['target'],
        });
    }
    if (input.productId !== null && excludedProductIds.length > 0) {
        ctx.addIssue({
            code: 'custom',
            message: 'Only a category program can exclude products.',
            path: ['excludedProductIds'],
        });
    }
    if (new Set(excludedProductIds).size !== excludedProductIds.length) {
        ctx.addIssue({
            code: 'custom',
            message: 'A product can be excluded only once.',
            path: ['excludedProductIds'],
        });
    }
    for (const [index, tier] of input.tiers.entries()) {
        const previous = input.tiers[index - 1];
        if (previous && tier.minCases <= previous.minCases) {
            ctx.addIssue({
                code: 'custom',
                message: `${VOLUME_DISCOUNT_CONSTRAINTS.minCases.label} must be higher than the previous tier.`,
                path: ['tiers', index, 'minCases'],
            });
        }
        if (input.priceAdjustmentTypeId === PriceAdjustmentType.PERCENTAGE &&
            tier.value > VOLUME_DISCOUNT_CONSTRAINTS.value.maxPercent) {
            ctx.addIssue({
                code: 'custom',
                message: `A percentage markdown must be ${VOLUME_DISCOUNT_CONSTRAINTS.value.maxPercent} or less.`,
                path: ['tiers', index, 'value'],
            });
        }
    }
});
//# sourceMappingURL=save-program-input.js.map